/**
 * G4 / V24 provider cost-efficiency analysis. Pure functions, no I/O; runs on the endpoint.
 *
 * Effective cost per task is computed per provider / model / account and per task type:
 *  - API billing: the run's own cost, `reported` by the CLI, else an `estimate` from tokens with
 *    the dated price table (`@viko/protocol` pricing.ts). Unpriced runs are `missing`.
 *  - Subscription billing (price configured per account): the subscription cost for the window is
 *    amortized over the tokens actually used, so each run carries its token share. Idle capacity
 *    is reported separately (utilization), never hidden inside the per-outcome figure.
 *  - Money kinds are never blended into one silent number (M15): reported, estimated and
 *    subscription-amortized spend are kept apart, `missing` runs are counted, and a cost per
 *    outcome carries its coverage.
 *  - Quality: an outcome is a landed task (one credited run per task; every attempt's spend still
 *    counts). Corrections, where the caller has them, down-weight an outcome to 1 / (1 + n).
 */
import {
  estimateModelUsd,
  PRICE_TABLE_AS_OF,
  type RoutingCandidate,
  type RoutingRecommendation,
  RoutingRecommendations,
} from '@viko/protocol'
import { type SessionUsage, totalTokens } from './usage-accounting.js'

export type Billing = { kind: 'subscription'; monthlyUsd: number } | { kind: 'api' }
export type Provider = 'claude' | 'codex'
export type CostBasis = 'reported' | 'estimate' | 'subscription' | 'mixed' | 'none'

export interface EfficiencyRun {
  runId: string
  agent: Provider
  /** Provider account; the agent name stands in for an unnamed default account. */
  accountId: string
  model?: string
  taskId?: string
  title?: string
  updatedAt: number
  usage: SessionUsage
  /** Ad hoc run (no task) that landed by itself. */
  landed?: boolean
}

export interface EfficiencyTask {
  id: string
  title: string
  labels: string[]
  status: string
  landedAt: number | null
  runId: string | null
}

export interface EfficiencyInput {
  now: number
  windowDays: number
  runs: readonly EfficiencyRun[]
  tasks: readonly EfficiencyTask[]
  /** Per account; an absent entry means billing is unknown (costs are shown as API-equivalent). */
  billing: Readonly<Record<string, Billing | undefined>>
  /** Correction counts by task id, where available. */
  corrections?: Readonly<Record<string, number>>
  /** Average used quota per account over the window (hub metrics), when known. */
  utilization?: Readonly<Record<string, { avgUsedPct: number; samples: number }>>
}

export interface EfficiencyGroup {
  key: string
  label: string
  runIds: string[]
  runs: number
  tokens: number
  reportedUsd: number
  estimatedUsd: number
  subscriptionUsd: number
  /** What the same tokens would cost on API list prices (reported else estimate); subscription runs included. */
  apiEquivalentUsd: number
  missingRuns: number
  /** Share of runs with any cost figure. */
  coverage: number
  costBasis: CostBasis
  outcomes: number
  qualityAdjustedOutcomes: number
  correctionsKnown: boolean
  corrections: number
  effectiveUsd: number
  /** null when there is no landed outcome or no cost figure: unknown, never zero. */
  usdPerOutcome: number | null
  usdPerQualityAdjustedOutcome: number | null
}

export interface TaskTypeReport {
  type: string
  total: EfficiencyGroup
  candidates: EfficiencyGroup[]
}

export interface SubscriptionReport {
  accountId: string
  provider: Provider
  monthlyUsd: number
  windowCostUsd: number
  tokens: number
  apiEquivalentUsd: number
  /** API-equivalent value divided by subscription cost; > 1 means the subscription beat API pricing. */
  valueRatio: number | null
  verdict: 'cheaper_than_api' | 'costlier_than_api' | 'unknown'
  utilizationPct: number | null
  utilizationSamples: number
  idleUsd: number | null
  outcomes: number
  usdPerOutcome: number | null
  /** Cost per outcome if the whole quota had been used at the current per-token yield. */
  usdPerOutcomeAtFullUse: number | null
  coverage: number
}

export interface EfficiencyReport {
  windowDays: number
  priceTableAsOf: string
  overall: EfficiencyGroup
  byProvider: EfficiencyGroup[]
  byModel: EfficiencyGroup[]
  byAccount: EfficiencyGroup[]
  byTaskType: TaskTypeReport[]
  subscriptions: SubscriptionReport[]
  recommendations: RoutingRecommendation[]
  /** Accounts with runs but no billing set: their costs are API-equivalent, not a charge. */
  billingUnknown: string[]
}

const DAY = 86_400_000

// ---- task types ---------------------------------------------------------------------------

const LABEL_TYPES: Record<string, string> = {
  bug: 'bugfix',
  bugfix: 'bugfix',
  fix: 'bugfix',
  feature: 'feature',
  feat: 'feature',
  refactor: 'refactor',
  test: 'test',
  tests: 'test',
  docs: 'docs',
  doc: 'docs',
  ui: 'ui',
  design: 'ui',
  chore: 'chore',
  ci: 'chore',
  build: 'chore',
  perf: 'refactor',
}

/** Task type from labels, a conventional-commit prefix, then title keywords; else `other`. */
export function taskTypeOf(t: { title?: string; labels?: readonly string[] }): string {
  for (const l of t.labels ?? []) {
    const hit = LABEL_TYPES[l.toLowerCase().replace(/^type[:/]/, '')]
    if (hit) return hit
  }
  const title = t.title ?? ''
  const prefix = /^\s*([a-z]+)(?:\([^)]*\))?!?:/i.exec(title)?.[1]?.toLowerCase()
  if (prefix && LABEL_TYPES[prefix]) return LABEL_TYPES[prefix] as string
  if (/\b(fix|bug|crash|regression)/i.test(title)) return 'bugfix'
  if (/\b(refactor|rename|clean ?up)/i.test(title)) return 'refactor'
  if (/\b(tests?|spec)\b/i.test(title)) return 'test'
  if (/\b(docs?|readme)\b/i.test(title)) return 'docs'
  if (/\b(ui|design|style|layout|css)\b/i.test(title)) return 'ui'
  return 'other'
}

// ---- per-run cost -------------------------------------------------------------------------

interface RunCost {
  basis: 'reported' | 'estimate' | 'subscription' | 'missing'
  usd: number
  apiEquivalentUsd: number | null
  tokens: number
}

function apiEquivalent(r: EfficiencyRun): { usd: number; kind: 'reported' | 'estimate' } | null {
  const c = r.usage.charge
  if (c.kind === 'reported' || c.kind === 'estimate') return { usd: c.usd, kind: c.kind }
  const t = r.usage.tokens
  if (t && !r.usage.partial) {
    const usd = estimateModelUsd(t, r.model)
    if (usd !== undefined) return { usd, kind: 'estimate' }
  }
  return null
}

function runCosts(input: EfficiencyInput, runs: readonly EfficiencyRun[]): Map<string, RunCost> {
  const tokensByAccount = new Map<string, number>()
  for (const r of runs)
    if (r.usage.tokens)
      tokensByAccount.set(
        r.accountId,
        (tokensByAccount.get(r.accountId) ?? 0) + totalTokens(r.usage.tokens),
      )
  const out = new Map<string, RunCost>()
  for (const r of runs) {
    const api = apiEquivalent(r)
    const tokens = r.usage.tokens ? totalTokens(r.usage.tokens) : 0
    const billing = input.billing[r.accountId]
    if (billing?.kind === 'subscription') {
      const pool = tokensByAccount.get(r.accountId) ?? 0
      const windowCost = (billing.monthlyUsd * input.windowDays) / 30
      out.set(
        r.runId,
        pool > 0 && tokens > 0
          ? {
              basis: 'subscription',
              usd: (windowCost * tokens) / pool,
              apiEquivalentUsd: api?.usd ?? null,
              tokens,
            }
          : { basis: 'missing', usd: 0, apiEquivalentUsd: api?.usd ?? null, tokens },
      )
    } else
      out.set(
        r.runId,
        api
          ? { basis: api.kind, usd: api.usd, apiEquivalentUsd: api.usd, tokens }
          : { basis: 'missing', usd: 0, apiEquivalentUsd: null, tokens },
      )
  }
  return out
}

// ---- outcomes -----------------------------------------------------------------------------

interface Outcome {
  runId: string
  taskId?: string
  weight: number
  known: boolean
  corrections: number
}

function outcomesOf(input: EfficiencyInput, runs: readonly EfficiencyRun[]): Outcome[] {
  const byId = new Map(runs.map((r) => [r.runId, r]))
  const byTask = new Map<string, EfficiencyRun[]>()
  for (const r of runs) if (r.taskId) byTask.set(r.taskId, [...(byTask.get(r.taskId) ?? []), r])
  const out: Outcome[] = []
  const weigh = (taskId: string | undefined) => {
    const c = taskId ? input.corrections?.[taskId] : undefined
    return c === undefined
      ? { weight: 1, known: false, corrections: 0 }
      : { weight: 1 / (1 + c), known: true, corrections: c }
  }
  for (const t of input.tasks) {
    if (t.landedAt == null) continue // "done" without a landing is not a verified outcome
    const attempts = byTask.get(t.id) ?? []
    const credited =
      (t.runId ? byId.get(t.runId) : undefined) ??
      [...attempts].sort((a, b) => b.updatedAt - a.updatedAt)[0]
    if (credited) out.push({ runId: credited.runId, taskId: t.id, ...weigh(t.id) })
  }
  const taskOutcome = new Set(out.map((o) => o.runId))
  for (const r of runs)
    if (r.landed && !r.taskId && !taskOutcome.has(r.runId))
      out.push({ runId: r.runId, weight: 1, known: false, corrections: 0 })
  return out
}

// ---- grouping -----------------------------------------------------------------------------

function group(
  key: string,
  label: string,
  runs: readonly EfficiencyRun[],
  costs: ReadonlyMap<string, RunCost>,
  outcomes: readonly Outcome[],
): EfficiencyGroup {
  const ids = new Set(runs.map((r) => r.runId))
  let reported = 0,
    estimated = 0,
    subscription = 0,
    apiEq = 0,
    missing = 0,
    tokens = 0
  const bases = new Set<string>()
  for (const r of runs) {
    const c = costs.get(r.runId)
    if (!c) continue
    tokens += c.tokens
    apiEq += c.apiEquivalentUsd ?? 0
    if (c.basis === 'missing') missing++
    else {
      bases.add(c.basis)
      if (c.basis === 'reported') reported += c.usd
      else if (c.basis === 'estimate') estimated += c.usd
      else subscription += c.usd
    }
  }
  const mine = outcomes.filter((o) => ids.has(o.runId))
  const qa = mine.reduce((n, o) => n + o.weight, 0)
  const known = mine.length > 0 && mine.every((o) => o.known)
  const effective = reported + estimated + subscription
  const costed = runs.length - missing
  return {
    key,
    label,
    runIds: runs.map((r) => r.runId),
    runs: runs.length,
    tokens,
    reportedUsd: reported,
    estimatedUsd: estimated,
    subscriptionUsd: subscription,
    apiEquivalentUsd: apiEq,
    missingRuns: missing,
    coverage: runs.length ? costed / runs.length : 0,
    costBasis:
      bases.size === 0 ? 'none' : bases.size === 1 ? ([...bases][0] as CostBasis) : 'mixed',
    outcomes: mine.length,
    qualityAdjustedOutcomes: qa,
    correctionsKnown: known,
    corrections: mine.reduce((n, o) => n + o.corrections, 0),
    effectiveUsd: effective,
    usdPerOutcome: mine.length && costed ? effective / mine.length : null,
    usdPerQualityAdjustedOutcome: qa > 0 && costed ? effective / qa : null,
  }
}

function bucket<T>(items: readonly T[], keyOf: (t: T) => string): Map<string, T[]> {
  const m = new Map<string, T[]>()
  for (const i of items) m.set(keyOf(i), [...(m.get(keyOf(i)) ?? []), i])
  return m
}

const candidateKey = (r: EfficiencyRun) => `${r.agent}/${r.model ?? 'unknown model'}`

function byUsdPerOutcome(a: EfficiencyGroup, b: EfficiencyGroup): number {
  return (
    (a.usdPerQualityAdjustedOutcome ?? Number.POSITIVE_INFINITY) -
      (b.usdPerQualityAdjustedOutcome ?? Number.POSITIVE_INFINITY) || b.runs - a.runs
  )
}

// ---- recommendations ----------------------------------------------------------------------

const candidateOf = (key: string): RoutingCandidate => {
  const i = key.indexOf('/')
  const model = key.slice(i + 1)
  return {
    provider: key.slice(0, i) as Provider,
    ...(model && model !== 'unknown model' ? { model } : {}),
  }
}
const usd = (n: number) => (n < 0.01 ? '<$0.01' : `$${n.toFixed(2)}`)

function recommend(
  type: string,
  candidates: readonly EfficiencyGroup[],
  runsByType: readonly EfficiencyRun[],
  costs: ReadonlyMap<string, RunCost>,
  outcomes: readonly Outcome[],
): RoutingRecommendation | undefined {
  const ranked = candidates
    .filter((g) => g.usdPerQualityAdjustedOutcome !== null && g.outcomes > 0)
    .sort(byUsdPerOutcome)
  const best = ranked[0],
    next = ranked[1]
  if (!best || !next) return undefined
  const bestCost = best.usdPerQualityAdjustedOutcome as number
  const nextCost = next.usdPerQualityAdjustedOutcome as number
  if (nextCost <= 0) return undefined
  const floor = Math.min(best.outcomes, next.outcomes)
  const confidence = floor >= 8 ? 'high' : floor >= 3 ? 'medium' : 'low'
  // Cheapest account for the winner, only when accounts actually differ.
  let accountId: string | undefined
  const mine = runsByType.filter((r) => candidateKey(r) === best.key)
  const accounts = [...bucket(mine, (r) => r.accountId)]
    .map(([id, rs]) => group(id, id, rs, costs, outcomes))
    .filter((g) => g.usdPerQualityAdjustedOutcome !== null && g.outcomes > 0)
    .sort(byUsdPerOutcome)
  const [a0, a1] = accounts
  if (
    a0 &&
    a1 &&
    (a0.usdPerQualityAdjustedOutcome as number) < (a1.usdPerQualityAdjustedOutcome as number)
  )
    accountId = a0.key
  const costBasis = best.costBasis === 'none' ? 'mixed' : best.costBasis
  const reason = `${best.label} cost ${usd(bestCost)} per verified ${type} outcome versus ${usd(nextCost)} for ${next.label}, over ${best.outcomes} and ${next.outcomes} landed tasks (${costBasis === 'mixed' ? 'mixed cost kinds' : `${costBasis} costs`}${best.coverage < 1 ? `, ${Math.round(best.coverage * 100)}% cost coverage` : ''}).`
  return {
    taskType: type,
    preferred: candidateOf(best.key),
    ...(accountId ? { accountId } : {}),
    evidence: {
      runs: best.runs,
      verifiedOutcomes: best.outcomes,
      qualityAdjustedOutcomes: best.qualityAdjustedOutcomes,
      correctionsKnown: best.correctionsKnown,
      costBasis,
      costCoverage: best.coverage,
      usdPerQualityAdjustedOutcome: bestCost,
    },
    beat: {
      candidate: candidateOf(next.key),
      cheaperPct: Math.round(((nextCost - bestCost) / nextCost) * 1000) / 10,
    },
    confidence,
    reason: reason.slice(0, 500),
  }
}

// ---- the report ---------------------------------------------------------------------------

export function analyzeProviderEfficiency(input: EfficiencyInput): EfficiencyReport {
  const since = input.now - input.windowDays * DAY
  const runs = input.runs.filter((r) => r.updatedAt >= since)
  const costs = runCosts(input, runs)
  const outcomes = outcomesOf(input, runs)
  const grp = (k: string, l: string, rs: readonly EfficiencyRun[]) =>
    group(k, l, rs, costs, outcomes)
  const sorted = (m: Map<string, EfficiencyRun[]>, label: (k: string) => string) =>
    [...m].map(([k, rs]) => grp(k, label(k), rs)).sort(byUsdPerOutcome)

  const taskById = new Map(input.tasks.map((t) => [t.id, t]))
  const typeOf = (r: EfficiencyRun) => {
    const t = r.taskId ? taskById.get(r.taskId) : undefined
    return taskTypeOf(t ?? { ...(r.title ? { title: r.title } : {}) })
  }
  const byTaskType: TaskTypeReport[] = []
  const recommendations: RoutingRecommendation[] = []
  for (const [type, rs] of bucket(runs, typeOf)) {
    const candidates = sorted(bucket(rs, candidateKey), (k) => k)
    byTaskType.push({ type, total: grp(type, type, rs), candidates })
    const rec = recommend(type, candidates, rs, costs, outcomes)
    if (rec) recommendations.push(rec)
  }
  byTaskType.sort((a, b) => b.total.runs - a.total.runs)

  const accounts = bucket(runs, (r) => r.accountId)
  const subscriptions: SubscriptionReport[] = []
  for (const [accountId, rs] of accounts) {
    const billing = input.billing[accountId]
    if (billing?.kind !== 'subscription') continue
    const g = grp(accountId, accountId, rs)
    const windowCost = (billing.monthlyUsd * input.windowDays) / 30
    const util = input.utilization?.[accountId]
    const apiKnown = g.apiEquivalentUsd > 0
    const ratio = apiKnown && windowCost > 0 ? g.apiEquivalentUsd / windowCost : null
    subscriptions.push({
      accountId,
      provider: rs[0]?.agent ?? 'claude',
      monthlyUsd: billing.monthlyUsd,
      windowCostUsd: windowCost,
      tokens: g.tokens,
      apiEquivalentUsd: g.apiEquivalentUsd,
      valueRatio: ratio,
      verdict: ratio === null ? 'unknown' : ratio >= 1 ? 'cheaper_than_api' : 'costlier_than_api',
      utilizationPct: util ? util.avgUsedPct : null,
      utilizationSamples: util?.samples ?? 0,
      idleUsd: util ? windowCost * (1 - Math.min(100, util.avgUsedPct) / 100) : null,
      outcomes: g.outcomes,
      usdPerOutcome: g.usdPerOutcome,
      usdPerOutcomeAtFullUse:
        g.usdPerOutcome !== null && util && util.avgUsedPct > 0
          ? g.usdPerOutcome * (Math.min(100, util.avgUsedPct) / 100)
          : null,
      coverage: g.coverage,
    })
  }

  return {
    windowDays: input.windowDays,
    priceTableAsOf: PRICE_TABLE_AS_OF,
    overall: grp('all', 'All runs', runs),
    byProvider: sorted(
      bucket(runs, (r) => r.agent),
      (k) => k,
    ),
    byModel: sorted(bucket(runs, candidateKey), (k) => k),
    byAccount: sorted(accounts, (k) => k),
    byTaskType,
    subscriptions: subscriptions.sort((a, b) => a.accountId.localeCompare(b.accountId)),
    recommendations: recommendations.sort((a, b) => a.taskType.localeCompare(b.taskType)),
    billingUnknown: [...accounts.keys()].filter((id) => !input.billing[id]).sort(),
  }
}

/** The G11-consumable export (validated against the protocol schema). */
export function exportRoutingRecommendations(
  report: EfficiencyReport,
  now: number,
): RoutingRecommendations {
  return RoutingRecommendations.parse({
    version: 1,
    source: 'g4-cost-efficiency',
    generatedAt: now,
    windowDays: report.windowDays,
    priceTableAsOf: report.priceTableAsOf,
    recommendations: report.recommendations,
  })
}
